"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.materializedQueryRows = materializedQueryRows;
exports.datasetQueryRows = datasetQueryRows;
exports.localizeAppBaseQueryReader = localizeAppBaseQueryReader;
exports.localizeAppBaseRouteQueryReader = localizeAppBaseRouteQueryReader;
const database_identity_1 = require("@streamlain/database-identity");
const app_base_binding_read_1 = require("./app-base-binding-read");
const app_base_contract_1 = require("./app-base-contract");
const app_base_source_query_1 = require("./app-base-source-query");
const content_i18n_1 = require("./content-i18n");
const content_i18n_projection_1 = require("./content-i18n-projection");
const DEFAULT_ERRORS = {
    revisionChanged: () => new app_base_source_query_1.AppBaseSourceQueryError('APP_BASE_QUERY_REVISION_CHANGED', 'App locale source revision changed'),
    schemaIncomplete: () => new app_base_source_query_1.AppBaseSourceQueryError('APP_BASE_SOURCE_UNAVAILABLE', 'App translation schema is incomplete', { reason: 'translation_schema_incomplete' }),
    windowChanged: () => new app_base_source_query_1.AppBaseSourceQueryError('APP_BASE_QUERY_REVISION_CHANGED', 'Translation window changed during projection'),
    tooLarge: ({ sourceKey, bytes, limit }) => new app_base_source_query_1.AppBaseSourceQueryError('APP_BASE_SOURCE_UNAVAILABLE', `translations retain ${bytes} bytes, limit ${limit}`, { reason: 'stored_value_too_large', sourceKey }),
};
const encoder = new TextEncoder();
function materializedQueryRows(rows) {
    return rows.map(row => ({
        id: row.id,
        cells: Object.fromEntries(Object.entries(row.cells).map(([id, cell]) => [id, cell.value])),
    }));
}
function datasetQueryRows(rows) {
    return rows.map(row => ({
        id: row.id,
        cells: Object.fromEntries(Object.entries(row.cells).map(([id, value]) => [id, { value }])),
    }));
}
/** Locale transforms the admitted chunks before filtering and sorting. Only
 * translations for this bounded chunk are retained. Revision dependencies are
 * part of every transformed shard and query identity. */
function localizeAppBaseQueryReader(options) {
    const input = {
        ...options,
        errors: { ...DEFAULT_ERRORS, ...options.errors },
    };
    if (input.locale === input.defaultLocale ||
        input.source.source.role === 'translations')
        return input.reader;
    const { reader, translations } = input;
    const entityDbs = translationEntityDbs(reader, input.source, translations);
    const identity = (0, app_base_contract_1.appBaseSha256Hex)((0, app_base_source_query_1.appBaseQueryIdentity)([
        reader.accessScope,
        input.locale,
        input.defaultLocale,
        input.policy,
        translations?.reader.accessScope,
        translations?.reader.index,
    ]));
    const schemaKey = (0, app_base_contract_1.appBaseSha256Hex)(reader.index.schemaKey + identity);
    // Translation replaces only these cells; ids and raw values stay stored.
    const translated = new Set(reader.index.columns
        .filter(column => (0, content_i18n_1.isContentI18nTranslatableColumn)(column))
        .map(column => column.id));
    const revision = (value) => (0, app_base_contract_1.appBaseSha256Hex)(value + identity);
    return {
        ...reader,
        accessScope: identity,
        index: {
            ...reader.index,
            schemaKey,
            shards: reader.index.shards.map(shard => ({
                ...shard,
                revision: revision(shard.revision),
            })),
        },
        readChunk: async (shardId) => {
            const chunk = await reader.readChunk(shardId);
            const expected = reader.index.shards.find(shard => shard.shardId === shardId);
            if (!expected ||
                chunk.revision !== expected.revision ||
                chunk.schemaKey !== reader.index.schemaKey)
                throw input.errors.revisionChanged();
            const entries = [];
            if (translations && chunk.rows.length) {
                const retained = { bytes: 0 };
                for (let start = 0; start < chunk.rows.length; start += 32) {
                    entries.push(...(await readTranslationEntries({
                        ...input,
                        translations,
                        entityDbs,
                        retained,
                        match: {
                            name: 'entityRowId',
                            values: chunk.rows.slice(start, start + 32).map(row => row.id),
                        },
                    })));
                }
            }
            const sources = (0, content_i18n_projection_1.applyContentI18nToSources)({
                sources: {
                    [reader.index.sourceKey]: {
                        ...input.source,
                        rows: materializedQueryRows(chunk.rows),
                    },
                    ...(translations
                        ? {
                            [translations.reader.index.sourceKey]: {
                                ...translations.source,
                                rows: materializedQueryRows(entries),
                            },
                        }
                        : {}),
                },
                locale: input.locale,
                defaultLocale: input.defaultLocale,
                policy: input.policy,
                apps: translations?.app ? [translations.app] : [],
            });
            const result = {
                ...chunk,
                schemaKey,
                revision: revision(chunk.revision),
                rows: datasetQueryRows(sources[reader.index.sourceKey].rows),
            };
            return result;
        },
        ...(reader.shardValueSummary
            ? {
                shardValueSummary: (shardId) => (0, database_identity_1.remapDatabaseShardValueColumns)(reader.shardValueSummary?.(shardId), id => (translated.has(id) ? null : id)),
            }
            : {}),
    };
}
/** Translation rows name a table by its key, its block or its name in the App. */
function translationEntityDbs(reader, source, translations) {
    const appSourceKey = translations?.app?.appSourceKeys[reader.index.sourceKey];
    return [
        ...new Set([
            reader.index.sourceKey,
            source.source.blockId,
            ...(appSourceKey === undefined ? [] : [appSourceKey]),
        ]),
    ];
}
/** The translation rows of one locale for these tables whose column `match`
 * holds one of the values, read page by page within the chunk budget. */
async function readTranslationEntries(input) {
    const { reader } = input.translations;
    const column = (name) => {
        const found = reader.index.columns.find(column => column.name === name);
        if (!found)
            throw input.errors.schemaIncomplete();
        return found.id;
    };
    const anyOf = (name, values) => ({
        type: 'predicate-group',
        operator: 'or',
        predicates: values.map(value => ({
            type: 'column',
            propertyId: column(name),
            operator: 'equals',
            value,
        })),
    });
    const predicates = [
        {
            type: 'column',
            propertyId: column('locale'),
            operator: 'equals',
            value: input.locale,
        },
        anyOf('entityDb', input.entityDbs),
        anyOf(input.match.name, input.match.values),
    ];
    const entries = [];
    for (let offset = 0;;) {
        const page = await input.queryPage(reader, {
            predicates,
            offset,
            limit: Math.min(app_base_binding_read_1.APP_BASE_BINDING_WINDOW_ROWS, reader.maxWindowRows ?? app_base_binding_read_1.APP_BASE_BINDING_WINDOW_ROWS),
        });
        input.retained.bytes += encoder.encode(JSON.stringify(page.rows)).byteLength;
        if (input.retained.bytes > app_base_contract_1.APP_BASE_BUDGETS.maxReplicaChunkBytes)
            throw input.errors.tooLarge({
                sourceKey: reader.index.sourceKey,
                bytes: input.retained.bytes,
                limit: app_base_contract_1.APP_BASE_BUDGETS.maxReplicaChunkBytes,
            });
        entries.push(...page.rows);
        offset += page.rows.length;
        if (offset >= page.totalCount)
            break;
        if (page.rows.length === 0)
            throw input.errors.windowChanged();
    }
    return entries;
}
/**
 * Select and multi-select option labels of a live table in the locale of the
 * request: the query names returned option IDs from these columns, so the
 * columns carry the labels of the translation table once per reader.
 */
async function localizeOptionColumns(input) {
    const { reader, translations } = input;
    const optionColumns = reader.index.columns.filter(column => column.options?.length);
    if (optionColumns.length === 0)
        return reader;
    const entries = await readTranslationEntries({
        ...input,
        entityDbs: translationEntityDbs(reader, input.source, translations),
        match: {
            name: 'field',
            values: optionColumns.map(column => (0, content_i18n_1.contentI18nOptionField)(column.id)),
        },
        retained: { bytes: 0 },
    });
    if (entries.length === 0)
        return reader;
    const localized = (0, content_i18n_projection_1.applyContentI18nToSources)({
        sources: {
            [reader.index.sourceKey]: {
                ...input.source,
                columns: optionColumns.map(({ id, name, type, options }) => ({
                    id,
                    name,
                    type,
                    data: { options },
                })),
                rows: [],
            },
            [translations.reader.index.sourceKey]: {
                ...translations.source,
                rows: materializedQueryRows(entries),
            },
        },
        locale: input.locale,
        defaultLocale: input.defaultLocale,
        policy: input.policy,
        apps: translations.app ? [translations.app] : [],
    })[reader.index.sourceKey].columns;
    const labels = new Map(localized.map(column => [
        column.id,
        column.data.options,
    ]));
    return {
        ...reader,
        index: {
            ...reader.index,
            columns: reader.index.columns.map(column => labels.has(column.id)
                ? { ...column, options: labels.get(column.id) }
                : column),
        },
    };
}
/**
 * Translates the rows a page of an App Container reads live into the locale
 * of the request: the one way of the server route, the author preview and the
 * internal App in the browser. The translation table is read only where the
 * locale differs and the container binds the table of the App the page
 * belongs to.
 */
async function localizeAppBaseRouteQueryReader(input) {
    const { reader, source, appBaseConfig, locale, defaultLocale } = input;
    const localization = {
        locale,
        defaultLocale,
        policy: appBaseConfig.i18nPolicy,
        queryPage: input.queryPage,
        errors: { ...DEFAULT_ERRORS, ...input.errors },
    };
    const app = (0, content_i18n_projection_1.contentI18nAppScopeOfRoute)((0, content_i18n_projection_1.resolveContentI18nAppScopes)(appBaseConfig), input.routePath, reader.index.sourceKey);
    const translationsSourceKey = app?.translationsSourceKey ?? 'translations';
    const translations = locale !== defaultLocale &&
        source.source.role !== 'translations' &&
        appBaseConfig.databaseBindings?.[translationsSourceKey]
        ? {
            ...(await input.readTranslations(translationsSourceKey)),
            app: app ?? undefined,
        }
        : undefined;
    return localizeAppBaseQueryReader({
        ...localization,
        reader: translations
            ? await localizeOptionColumns({
                ...localization,
                reader,
                source,
                translations,
            })
            : reader,
        source,
        translations,
    });
}
